/**
 * jXRL date-format router — three distinct date representations coexist in a
 * single jXRL request/response envelope. Sending the wrong format to the wrong
 * slot causes silent corruption (e.g. `00000000` writes) or Core Director
 * System Error 1 rejections.
 *
 * Authority:
 *   - memory/feedback_date_format_rules.md (Named JSON vs UDF rules)
 *   - memory/feedback_matdt_format.md      (MatDt numeric YYYYMMDD rule)
 *   - Audit ref: G-CC-18 (01-cross-cutting-gaps.md)
 *
 * The three formats:
 *
 *   1. NAMED JSON DATE — ISO "YYYY-MM-DD".
 *      Used by named JSON elements that DataWeave (MuleSoft) serializes into
 *      SOAP date types: BenfBirthDt, CustDetail.BirthDt, OpenDt, NxtPayDt,
 *      IssDt, ExpDt, VerifyDt, etc.
 *      Sending YYYYMMDD here → System Error 1 (confirmed by v1.60.8 TaxPlnBenfAdd).
 *
 *   2. UDF BYPASS DATE — raw numeric "YYYYMMDD" (string).
 *      Used inside UserDefInfoArray entries where the value writes directly to
 *      Core Director MASTER.ISM date fields, bypassing DataWeave's type
 *      conversion. Core Director expects 8-digit numeric.
 *
 *   3. MATDT — numeric "YYYYMMDD".
 *      The TimeDep maturity date (`MatDt`) is a NAMED JSON element but Core
 *      Director declares it numeric length 8. DataWeave converts ISO here to
 *      `00000000` → error 1050 "Maturity date must be after Date Opened".
 *      Confirmed by JHA Developer AI 2026-04-02.
 *
 * Every v3 caller building a jXRL payload MUST route date fields through this
 * module. Never hand-format inline.
 */

// ─────────────────────────────────────────────────────────────────────────────
// Internal helpers
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Normalize a `Date | string` input into a concrete Date. Accepts:
 *   - Date objects (returned as-is)
 *   - ISO "YYYY-MM-DD" strings (parsed as local-date midnight)
 *   - Numeric "YYYYMMDD" strings
 *   - "MM/DD/YYYY" US short-date strings
 *   - Anything `Date` can parse as a fallback
 *
 * Returns null if the input cannot be interpreted as a valid calendar date.
 */
function toDate(input: Date | string | null | undefined): Date | null {
  if (input instanceof Date) {
    return isNaN(input.getTime()) ? null : input;
  }
  if (typeof input !== 'string') return null;
  const s = input.trim();
  if (!s) return null;

  // ISO YYYY-MM-DD (or YYYY-MM-DDTHH:mm:ss...) — parse manually to avoid TZ drift.
  const isoMatch = /^(\d{4})-(\d{2})-(\d{2})(?:[T ].*)?$/.exec(s);
  if (isoMatch) {
    const y = Number(isoMatch[1]);
    const m = Number(isoMatch[2]);
    const d = Number(isoMatch[3]);
    return buildDate(y, m, d);
  }

  // Numeric YYYYMMDD.
  if (/^\d{8}$/.test(s)) {
    const y = Number(s.substring(0, 4));
    const m = Number(s.substring(4, 6));
    const d = Number(s.substring(6, 8));
    return buildDate(y, m, d);
  }

  // MM/DD/YYYY US short date; ID scans also return MM-DD-YYYY and MM.DD.YYYY (CHG-0107, 2026-11-02).
  const usMatch = /^(\d{1,2})[/.-](\d{1,2})[/.-](\d{4})$/.exec(s);
  if (usMatch) {
    const m = Number(usMatch[1]);
    const d = Number(usMatch[2]);
    const y = Number(usMatch[3]);
    return buildDate(y, m, d);
  }

  // Last-chance fallback: let JS try.
  const parsed = new Date(s);
  return isNaN(parsed.getTime()) ? null : parsed;
}

/**
 * Build a local-timezone Date from Y/M/D ints, returning null if the calendar
 * round-trip fails (e.g. Feb 30 → March rollover).
 */
function buildDate(year: number, month: number, day: number): Date | null {
  if (!Number.isFinite(year) || !Number.isFinite(month) || !Number.isFinite(day)) return null;
  if (month < 1 || month > 12 || day < 1 || day > 31) return null;
  const d = new Date(year, month - 1, day);
  if (d.getFullYear() !== year || d.getMonth() !== month - 1 || d.getDate() !== day) return null;
  return d;
}

/** Left-pad a non-negative integer with zeros to the requested width. */
function pad(n: number, width: number): string {
  const s = String(n);
  return s.length >= width ? s : '0'.repeat(width - s.length) + s;
}

/** Format a Date as ISO "YYYY-MM-DD" using local calendar fields (no UTC shift). */
function formatIso(d: Date): string {
  return pad(d.getFullYear(), 4) + '-' + pad(d.getMonth() + 1, 2) + '-' + pad(d.getDate(), 2);
}

/** Format a Date as numeric "YYYYMMDD" using local calendar fields. */
function formatYmd(d: Date): string {
  return pad(d.getFullYear(), 4) + pad(d.getMonth() + 1, 2) + pad(d.getDate(), 2);
}

// ─────────────────────────────────────────────────────────────────────────────
// 1) Named JSON dates — ISO "YYYY-MM-DD"
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Format a date for a NAMED JSON element (DOB, OpenDt, BenfBirthDt, IssDt, etc.).
 * Output: ISO `"YYYY-MM-DD"`.
 *
 * Returns `""` for invalid input (callers that need to omit the field entirely
 * should check for `""` and skip the property assignment).
 *
 * @example
 *   toNamedJsonDate(new Date(2026, 3, 21))  // "2026-04-21"
 *   toNamedJsonDate("20260421")             // "2026-04-21"
 *   toNamedJsonDate("2026-04-21")           // "2026-04-21"
 */
export function toNamedJsonDate(d: Date | string): string {
  const date = toDate(d);
  return date ? formatIso(date) : '';
}

/**
 * Parse a named JSON date field back into a Date. Accepts ISO, numeric
 * YYYYMMDD, or US MM/DD/YYYY just in case a wayward payload slipped through.
 * Returns null on any falsy or unparseable input.
 */
export function fromNamedJsonDate(s: string | null | undefined): Date | null {
  return toDate(s);
}

// ─────────────────────────────────────────────────────────────────────────────
// 2) UDF bypass dates — raw numeric "YYYYMMDD" string
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Format a date for a UDF bypass slot inside `UserDefInfoArray`. Output is
 * the 8-digit numeric string `"YYYYMMDD"` that writes directly to Core Director
 * without DataWeave intervention.
 *
 * Returns `""` for invalid input.
 *
 * @example
 *   toUdfDate(new Date(2026, 3, 21))  // "20260421"
 *   toUdfDate("2026-04-21")           // "20260421"
 */
export function toUdfDate(d: Date | string): string {
  const date = toDate(d);
  return date ? formatYmd(date) : '';
}

/**
 * Parse a UDF date value (typically "YYYYMMDD") back into a Date.
 */
export function fromUdfDate(s: string | null | undefined): Date | null {
  return toDate(s);
}

// ─────────────────────────────────────────────────────────────────────────────
// 3) MatDt — numeric YYYYMMDD as a `number` (NOT a string, NOT ISO)
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Format a date for the TimeDep `MatDt` (maturity date) slot. Core Director
 * declares this field as NUMERIC length 8 — DataWeave converts any ISO input
 * here to `00000000` and Core Director rejects with error 1050 ("Maturity
 * date must be after Date Opened").
 *
 * Returns the date as a JS `number` (e.g. `20260421`). Callers should inline
 * this into the payload as a number, not a string.
 *
 * Returns `0` for invalid input — callers should treat 0 as "do not send".
 *
 * @example
 *   toMatDt(new Date(2026, 3, 21))  // 20260421
 *   toMatDt("2026-04-21")           // 20260421
 *   toMatDt("invalid")              // 0
 */
export function toMatDt(d: Date | string): number {
  const date = toDate(d);
  if (!date) return 0;
  return Number(formatYmd(date));
}

/**
 * Parse a MatDt value back into a Date. Accepts both numeric and string
 * forms since JSON sometimes preserves and sometimes stringifies numerics.
 */
export function fromMatDt(n: number | string | null | undefined): Date | null {
  if (n == null) return null;
  if (typeof n === 'number') {
    if (!Number.isFinite(n) || n <= 0) return null;
    return toDate(String(n));
  }
  return toDate(n);
}

// ─────────────────────────────────────────────────────────────────────────────
// Core Director generic date helpers
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Parse a Core Director date string that may arrive as either `YYYYMMDD`
 * (legacy ISM field) or `YYYY-MM-DD` (jXRL-normalized). Both shapes appear in
 * the wild across different operations, so every reader needs a forgiving parser.
 *
 * Returns null on any falsy or unparseable input.
 */
export function parseCoreDate(s: string | null | undefined): Date | null {
  return toDate(s);
}

/**
 * Format a Date for UI display in ISO `"YYYY-MM-DD"`. Returns `""` for null
 * / undefined / invalid input so renderers can safely drop in the result.
 */
export function formatCoreDate(d: Date | null | undefined): string {
  if (!d) return '';
  if (!(d instanceof Date) || isNaN(d.getTime())) return '';
  return formatIso(d);
}
